
export const PassScoreStatus = 'pass';
export const FailScoreStatus = 'fail';
export const WarnScoreStatus = 'warn';
export const ExceedScoreStatus = 'exceed';
export type ScoreStatus = 'exceed' | 'pass' | 'warn' | 'fail';

type PasswordRequirementAdditionNames = 'NumberOfCharacters'
    | 'UppercaseLetters' | 'LowercaseLetters'| 'Numbers'| 'Symbols'
    | 'MiddleNumbersOrSymbols' | 'Requirements';
type PasswordRequirementDeductionNames = 'LettersOnly' | 'NumbersOnly' | 'RepeatCharacters'
    | 'ConsecutiveUppercaseLetters' | 'ConsecutiveLowercaseLetters' | 'ConsecutiveNumbers'
    | 'SequentialLetters' | 'SequentialNumbers' | 'SequentialSymbols' ;
export type PasswordRequirementNames = PasswordRequirementAdditionNames | PasswordRequirementDeductionNames;

export interface PasswordScore{
    calculatedAsANumber: number
    requirements: {[key in PasswordRequirementNames]: PasswordRequirement}
}
export interface PasswordRequirement{
    name: PasswordRequirementNames
    status: ScoreStatus
    count: number
    score: number
}
const DefaultFailRequirement: PasswordRequirement = {
    name: 'Symbols',
    status: FailScoreStatus,
    count: 0,
    score: 0,
}
const DefaultPassedRequirement: PasswordRequirement = {
    name: 'Symbols',
    status: PassScoreStatus,
    count: 0,
    score: 0,
}

export const DefaultPasswordScore: PasswordScore= {
    calculatedAsANumber: 0,
    requirements:{
        'NumberOfCharacters': {...DefaultFailRequirement, name:'NumberOfCharacters'},
        'UppercaseLetters': {...DefaultFailRequirement, name:'UppercaseLetters'} ,
        'LowercaseLetters': {...DefaultFailRequirement, name:'LowercaseLetters'},
        'Numbers': {...DefaultFailRequirement, name:'Numbers'},
        'Symbols': {...DefaultFailRequirement, name:'Symbols'},

        'MiddleNumbersOrSymbols': {...DefaultFailRequirement, name:'MiddleNumbersOrSymbols'} ,
        'Requirements': {...DefaultFailRequirement, name:'Requirements'},

        'LettersOnly': {...DefaultPassedRequirement, name:'LettersOnly'} ,
        'NumbersOnly': {...DefaultPassedRequirement, name:'NumbersOnly'} ,

        'RepeatCharacters': {...DefaultPassedRequirement, name:'RepeatCharacters'},

        'ConsecutiveUppercaseLetters': {...DefaultPassedRequirement, name:'ConsecutiveUppercaseLetters'},
        'ConsecutiveLowercaseLetters': {...DefaultPassedRequirement, name:'ConsecutiveLowercaseLetters'},
        'ConsecutiveNumbers': {...DefaultPassedRequirement, name:'ConsecutiveNumbers'},

        'SequentialLetters': {...DefaultPassedRequirement, name:'SequentialLetters'},
        'SequentialNumbers': {...DefaultPassedRequirement, name:'SequentialNumbers'} ,
        'SequentialSymbols': {...DefaultPassedRequirement, name:'SequentialSymbols'},
    }
};

const passwordScoreMultipliers = {
    SequentialLetters: 3,
    SequentialNumber: 3,
    SequentialSymbol: 3,
    ConsecutiveNumbers: 2,
    ConsecutiveUpperCaseLetters: 2,
    ConsecutiveLowerCaseLetters: 2,
    BaseLength: 4,
    NumbersInPassword: 4,
    SymbolsInPassword: 6,
    IncrementalNumbers: 2,
}
const allUpperCaseLettersRegex = /[A-Z]/g;
const allLowerCaseLettersRegex = /[a-z]/g;
const allNumbersRegex = /[0-9]/g;
const allSymbolsRegex = /[^a-zA-Z0-9_]/g;

interface CardinalityStateOfPasswordCharLookUp{
    lastUpperCaseLetterCharValue: string
    UpperCaseLetterCount: number
    ConsecutiveUpperCaseLetterCount: number

    lastLowerCaseLetterCharValue: string
    ConsecutiveLowerCaseLetterCount: number
    LowerCaseLetterCount: number

    lastNumberCharValue: string
    ConsecutiveNumberCount: number
    numberCount: number

    lastSymbolCharValue: string,
    ConsecutiveSymbolCount: number
    symbolCount: number

    ConsecutiveCount: number
    middleCharacterCount: number
    repetitionIncrement: number
    repetitionCharacter: number
    passwordLength: number
}

function determineCardinalityOfPassword(password: string): CardinalityStateOfPasswordCharLookUp{
    const cardinality: CardinalityStateOfPasswordCharLookUp = {
        lastUpperCaseLetterCharValue: '',
        lastLowerCaseLetterCharValue: '',
        lastNumberCharValue: '',
        lastSymbolCharValue: '',
        ConsecutiveSymbolCount: 0,
        UpperCaseLetterCount: 0,
        ConsecutiveUpperCaseLetterCount:0,
        ConsecutiveLowerCaseLetterCount: 0,
        LowerCaseLetterCount: 0,
        ConsecutiveNumberCount: 0,
        ConsecutiveCount: 0,
        middleCharacterCount: 0,
        numberCount: 0,
        symbolCount: 0,
        repetitionIncrement: 0,
        repetitionCharacter: 0,
        passwordLength: password.length
    }
    // Loop through password to check for Symbol, Numeric, Lowercase and Uppercase pattern matches
    const stringArrayOfPassword = password.replace(/\s+/g, "").split(/\s*/)

    for (let indexOfCharValue = 0; indexOfCharValue < cardinality.passwordLength; indexOfCharValue++) {
        if (stringArrayOfPassword[indexOfCharValue].match(allUpperCaseLettersRegex)) {
            if (cardinality.lastUpperCaseLetterCharValue !== "") {
                if ((parseInt(cardinality.lastUpperCaseLetterCharValue) + 1) === indexOfCharValue) {
                    cardinality.ConsecutiveUpperCaseLetterCount++
                    cardinality.ConsecutiveCount++
                }
            }
            cardinality.lastUpperCaseLetterCharValue = String(indexOfCharValue)
            cardinality.UpperCaseLetterCount++
        }
        else if (stringArrayOfPassword[indexOfCharValue].match(allLowerCaseLettersRegex)) {
            if (cardinality.lastLowerCaseLetterCharValue !== "") {
                if ((parseInt(cardinality.lastLowerCaseLetterCharValue) + 1) === indexOfCharValue) {
                    cardinality.ConsecutiveLowerCaseLetterCount++
                    cardinality.ConsecutiveCount++
                }
            }
            cardinality.lastLowerCaseLetterCharValue  = String(indexOfCharValue)
            cardinality.LowerCaseLetterCount ++
        }
        else if (stringArrayOfPassword[indexOfCharValue].match(allNumbersRegex)) {
            if (indexOfCharValue > 0 && indexOfCharValue < (cardinality.passwordLength - 1)) {
                cardinality.middleCharacterCount++
            }
            if (cardinality.lastNumberCharValue !== "") {
                if ((parseInt(cardinality.lastNumberCharValue) + 1) === indexOfCharValue) {
                    cardinality.ConsecutiveNumberCount++
                    cardinality.ConsecutiveCount++
                }
            }
            cardinality.lastNumberCharValue = String(indexOfCharValue)
            cardinality.numberCount++
        }
        else if (stringArrayOfPassword[indexOfCharValue].match(allSymbolsRegex)) {
            if (indexOfCharValue > 0 && indexOfCharValue < (cardinality.passwordLength - 1)) {
                cardinality.middleCharacterCount++
            }
            if (cardinality.lastSymbolCharValue !== "") {
                if ((parseInt(cardinality.lastSymbolCharValue) + 1) === indexOfCharValue) {
                    cardinality.ConsecutiveSymbolCount++
                    cardinality.ConsecutiveCount++
                }
            }
            cardinality.lastSymbolCharValue = String(indexOfCharValue)
            cardinality.symbolCount++
        }


        // Internal loop through password to check for repeat characters
        let repeatCharacterExists = false
        for (let repeatedChar = 0; repeatedChar < cardinality.passwordLength; repeatedChar++) {
            if (stringArrayOfPassword[indexOfCharValue] === stringArrayOfPassword[repeatedChar] && indexOfCharValue !== repeatedChar) {
                repeatCharacterExists = true

                 // Calculate increment deduction based on proximity to identical characters
                 // Deduction is incremented each time a new match is discovered
                 // Deduction amount is based on total password length divided by the
                 // difference of distance between currently selected match
                cardinality.repetitionIncrement += Math.abs(cardinality.passwordLength / (repeatedChar - indexOfCharValue))
            }
        }
        if (repeatCharacterExists) {
            cardinality.repetitionCharacter++
            const numberForUniqueCharacters = cardinality.passwordLength - cardinality.repetitionCharacter
            cardinality.repetitionIncrement = (numberForUniqueCharacters) ?
                Math.ceil(cardinality.repetitionIncrement / numberForUniqueCharacters) :
                Math.ceil(numberForUniqueCharacters)
        }
    }
    return cardinality;
}

function determinePasswordScoreFromPasswordCardinality(initialScore: number, cardinality: CardinalityStateOfPasswordCharLookUp): number{
    const passwordLength = cardinality.passwordLength;
    let currentScore = initialScore;

    if (cardinality.UpperCaseLetterCount > 0 && cardinality.UpperCaseLetterCount < passwordLength) {
        currentScore = parseInt(String(currentScore + ((passwordLength - cardinality.UpperCaseLetterCount) * 2)), 10)
        // sAlphaUC = String((passwordLength - cardinality.UpperCaseLetterCount) * 2)
    }
    if (cardinality.LowerCaseLetterCount > 0 && cardinality.LowerCaseLetterCount < passwordLength) {
        currentScore = parseInt(String(currentScore + ((passwordLength - cardinality.LowerCaseLetterCount) * 2)), 10)
        // sAlphaLC = String((passwordLength - cardinality.LowerCaseLetterCount) * 2)
    }
    if (cardinality.numberCount > 0 && cardinality.numberCount < passwordLength) {
        currentScore = parseInt(String(currentScore + (cardinality.numberCount * passwordScoreMultipliers.NumbersInPassword)), 10)
        // sNumber = String(cardinality.numberCount * passwordScoreMultipliers.NumbersInPassword)
    }
    if (cardinality.symbolCount > 0) {
        currentScore = parseInt(String(currentScore + (cardinality.symbolCount * passwordScoreMultipliers.SymbolsInPassword)), 10)
        //sSymbol = String(cardinality.symbolCount * passwordScoreMultipliers.SymbolsInPassword)
    }
    if (cardinality.middleCharacterCount > 0) {
        currentScore = parseInt(String(currentScore + (cardinality.middleCharacterCount * passwordScoreMultipliers.IncrementalNumbers)), 10)
        //sMidChar = String(cardinality.middleCharacterCount * passwordScoreMultipliers.IncrementalNumbers)
    }
    return currentScore;
}

function reverseText(str: string): string{
    return str.split("").reverse().join("")
}
function CheckForSequentialPatterns(pattern: string, sizeOfPatternToCheck: number, password: string): number{
    // Check for sequential string patterns (forward and reverse)
    let sequenceCount = 0;
    for (let sequentialAlpha = 0; sequentialAlpha < sizeOfPatternToCheck; sequentialAlpha++) {
        let sFwd = pattern.substring(sequentialAlpha, parseInt(String(sequentialAlpha + 3), 10))
        let sRev = reverseText(sFwd)
        if (password.toLowerCase().indexOf(sFwd) !== -1 ||
            password.toLowerCase().indexOf(sRev) !== -1) {
            sequenceCount++;
        }
    }
    return sequenceCount;
}
interface SequenceChecks{
    AlphaSequenceDuplicates: number,
    NumericSequenceDuplicates: number,
    SequenceSymbolDuplicates: number,
    SequenceCount: number
}
function RunSequenceChecks(password: string): SequenceChecks{
    const result = {
        AlphaSequenceDuplicates: 0,// nSeqAlpha++
        NumericSequenceDuplicates:0,// nSeqNumber++
        SequenceSymbolDuplicates: 0,// nSeqSymbol++
        SequenceCount: 0,// nSeqChar++
    }
    const alphaSequence: string = "abcdefghijklmnopqrstuvwxyz"
    result.AlphaSequenceDuplicates = CheckForSequentialPatterns(alphaSequence,23,password);
    const numberSequence: string = "01234567890"
    result.NumericSequenceDuplicates = CheckForSequentialPatterns(numberSequence,8,password);
    let SequenceSymbols: string = ")!@#$%^&*()"
    result.SequenceSymbolDuplicates = CheckForSequentialPatterns(SequenceSymbols,8,password);

    result.SequenceCount = result.SequenceSymbolDuplicates+  result.NumericSequenceDuplicates+ result.AlphaSequenceDuplicates;
    return result;
}

const decimalNumberRadix = 10;
function checkIfPasswordMissesRequirements(cardinality: CardinalityStateOfPasswordCharLookUp, sequenceChecksResults:SequenceChecks, currentPasswordScore: number): PasswordScore{
    const result: PasswordScore = JSON.parse(JSON.stringify(DefaultPasswordScore));
    const passwordLength = cardinality.passwordLength;
    let passwordScore = currentPasswordScore;
    if ((cardinality.LowerCaseLetterCount > 0 || cardinality.UpperCaseLetterCount > 0) && cardinality.symbolCount === 0 && cardinality.numberCount === 0) {  // Only Letters
        passwordScore = parseInt(String(passwordScore - passwordLength), decimalNumberRadix);
        result.requirements["LettersOnly"].status = FailScoreStatus;
        result.requirements["LettersOnly"].count = passwordLength;
        result.requirements["LettersOnly"].score = passwordLength;
    }
    if (cardinality.LowerCaseLetterCount === 0 && cardinality.UpperCaseLetterCount === 0 && cardinality.symbolCount === 0 && cardinality.numberCount > 0) {  // Only Numbers
        passwordScore = parseInt(String(passwordScore - passwordLength), decimalNumberRadix);
        result.requirements["NumbersOnly"].status = FailScoreStatus;
        result.requirements["NumbersOnly"].count = passwordLength;
        result.requirements["NumbersOnly"].score = passwordLength;
    }

    if (cardinality.repetitionCharacter > 0) {  // Same character exists more than once
        passwordScore = parseInt(String(passwordScore - cardinality.repetitionIncrement), decimalNumberRadix);
        result.requirements["RepeatCharacters"].status = PassScoreStatus;
        result.requirements["RepeatCharacters"].count = cardinality.repetitionCharacter;
        result.requirements["RepeatCharacters"].score = cardinality.repetitionIncrement;
    }
    if (cardinality.ConsecutiveUpperCaseLetterCount > 0) {  // Consecutive Uppercase Letters exist
        result.requirements["ConsecutiveUppercaseLetters"].score = cardinality.ConsecutiveUpperCaseLetterCount * passwordScoreMultipliers.ConsecutiveUpperCaseLetters;
        passwordScore = parseInt(String(passwordScore - result.requirements["ConsecutiveUppercaseLetters"].score), decimalNumberRadix);
        result.requirements["ConsecutiveUppercaseLetters"].count = cardinality.ConsecutiveUpperCaseLetterCount ;
        result.requirements["ConsecutiveUppercaseLetters"].status = FailScoreStatus;
    }
    if (cardinality.ConsecutiveLowerCaseLetterCount  > 0) {  // Consecutive Lowercase Letters exist
        result.requirements["ConsecutiveLowercaseLetters"].score = cardinality.ConsecutiveLowerCaseLetterCount * passwordScoreMultipliers.ConsecutiveLowerCaseLetters;
        passwordScore = parseInt(String(passwordScore - result.requirements["ConsecutiveLowercaseLetters"].score ), decimalNumberRadix);
        result.requirements["ConsecutiveLowercaseLetters"].count = cardinality.ConsecutiveLowerCaseLetterCount ;
        result.requirements["ConsecutiveLowercaseLetters"].status = FailScoreStatus;
    }
    if (cardinality.ConsecutiveNumberCount  > 0) {  // Consecutive Numbers exist
        result.requirements["ConsecutiveNumbers"].score = cardinality.ConsecutiveNumberCount * passwordScoreMultipliers.ConsecutiveNumbers;
        passwordScore = parseInt(String(passwordScore -  result.requirements["ConsecutiveNumbers"].score), decimalNumberRadix);
        result.requirements["ConsecutiveNumbers"].count = cardinality.ConsecutiveNumberCount ;
        result.requirements["ConsecutiveNumbers"].status = FailScoreStatus;
    }

    if (sequenceChecksResults.AlphaSequenceDuplicates > 0) {  // Sequential alpha strings exist (3 characters or more)
        result.requirements["SequentialLetters"].score = sequenceChecksResults.AlphaSequenceDuplicates * passwordScoreMultipliers.SequentialLetters;
        passwordScore = parseInt(String(passwordScore -  result.requirements["SequentialLetters"].score), decimalNumberRadix);
        result.requirements["SequentialLetters"].count = sequenceChecksResults.AlphaSequenceDuplicates ;
        result.requirements["SequentialLetters"].status = FailScoreStatus;
    }

    if (sequenceChecksResults.NumericSequenceDuplicates > 0) {  // Sequential numeric strings exist (3 characters or more)
        result.requirements["SequentialNumbers"].score = sequenceChecksResults.NumericSequenceDuplicates * passwordScoreMultipliers.SequentialNumber;
        passwordScore = parseInt(String(passwordScore -  result.requirements["SequentialNumbers"].score), decimalNumberRadix);
        result.requirements["SequentialNumbers"].count = sequenceChecksResults.NumericSequenceDuplicates ;
        result.requirements["SequentialNumbers"].status = FailScoreStatus;

    }
    if (sequenceChecksResults.SequenceSymbolDuplicates > 0) {  // Sequential symbol strings exist (3 characters or more)
        result.requirements["SequentialSymbols"].score = sequenceChecksResults.SequenceSymbolDuplicates * passwordScoreMultipliers.SequentialSymbol;
        passwordScore = parseInt(String(passwordScore -  result.requirements["SequentialSymbols"].score), decimalNumberRadix);
        result.requirements["SequentialSymbols"].count = sequenceChecksResults.SequenceSymbolDuplicates ;
        result.requirements["SequentialSymbols"].status = FailScoreStatus;

    }
    let requirementsMet = 0;
    if (passwordLength === 8) {
        result.requirements["NumberOfCharacters"].status = PassScoreStatus;
        result.requirements["NumberOfCharacters"].count = passwordLength;
        requirementsMet++;
    }
    else if (passwordLength > 8) {
        result.requirements["NumberOfCharacters"].status = ExceedScoreStatus;
        result.requirements["NumberOfCharacters"].count = passwordLength;
        requirementsMet++;
    }

    if(cardinality.UpperCaseLetterCount > 0){
        result.requirements["UppercaseLetters"].status = PassScoreStatus;
        result.requirements["UppercaseLetters"].count = cardinality.UpperCaseLetterCount;
        requirementsMet++;
    }
    if(cardinality.LowerCaseLetterCount > 0){
        result.requirements["LowercaseLetters"].status = PassScoreStatus;
        result.requirements["LowercaseLetters"].count = cardinality.LowerCaseLetterCount;
        requirementsMet++;
    }
    if(cardinality.numberCount > 0){
        result.requirements["Numbers"].status = PassScoreStatus;
        result.requirements["Numbers"].count = cardinality.numberCount;
        requirementsMet++;
    }
    if(cardinality.symbolCount > 0){
        result.requirements["Symbols"].status = PassScoreStatus;
        result.requirements["Symbols"].count = cardinality.symbolCount;
        requirementsMet++;
    }
    if(cardinality.middleCharacterCount > 0){
        result.requirements["MiddleNumbersOrSymbols"].status = PassScoreStatus;
        result.requirements["MiddleNumbersOrSymbols"].count = cardinality.middleCharacterCount;
        requirementsMet++;
    }
    const neededRequirements = passwordLength > 3 ? 3 : 4;
    result.requirements["Requirements"].count = requirementsMet;
    const metRequirements = (requirementsMet > neededRequirements);
    if(metRequirements){
        passwordScore = parseInt(String(passwordScore + (requirementsMet * 2)), decimalNumberRadix)
        result.requirements["Requirements"].status = (neededRequirements+1) > requirementsMet ?
            PassScoreStatus :
            ExceedScoreStatus;
    }
    result.calculatedAsANumber = passwordScore;
    return result;
}

export default function CalculatePasswordScore(pwd: string): PasswordScore {
    if (pwd) {
        let passwordScoreAsNumber = parseInt(String(pwd.length * passwordScoreMultipliers.BaseLength), 10);
        const passwordCardinality = determineCardinalityOfPassword(pwd);
        const sequenceChecks = RunSequenceChecks(pwd);
        passwordScoreAsNumber = determinePasswordScoreFromPasswordCardinality(passwordScoreAsNumber, passwordCardinality);
        return checkIfPasswordMissesRequirements(passwordCardinality, sequenceChecks, passwordScoreAsNumber);
    }
    return JSON.parse(JSON.stringify(DefaultPasswordScore));
}
